import { DASHBOARD_HUB_LINKS } from '@/@dashboards/hubs';
import { FEASIBILITY_CHOICES, STAGE_COLORS, VTRACKER_DASHBOARDS } from '@/@home/constants';
import { STUDIO_NAV_FEATURE_FLAGS } from '@/@studio/types/constants';
import { Routes } from '@/app/routes';
import { SLIDE_CREATOR_ALPHA_FLAG } from '@/lib/slide-creation/alpha-access';

export interface NavChildFlyoutItem {
    name: string;
    /** Iconify slug without the `lucide:` prefix. */
    icon: string;
    href: string;
    /** Where the row or tile itself goes while this choice is available (see `primaryChoiceHref`). */
    primary?: boolean;
    /** Only rendered when this feature flag is on — same contract as
     * `NavChild.requiresFlag`, for a flyout row that maps 1:1 to a
     * gated legacy-sidebar item (e.g. ABC/DTS Norms). */
    requiresFlag?: string;
    /** Rendered greyed-out and inert with a "Coming soon" tooltip, same
     * contract as `NavChild.comingSoon` — for a channel held back for
     * review rather than actually flag- or license-gated. Unlike
     * `requiresFlag`, this still renders the row (just disabled) instead
     * of dropping it from the flyout. */
    comingSoon?: boolean;
    /** Only rendered for internal viewers, or external viewers whose own
     * org name contains this string (case-insensitive) — for a
     * client-specific dashboard (e.g. a vTracker) with no flag or license
     * of its own to gate on. Dropped from the flyout entirely for anyone
     * else, same as a flag-gated row with the flag off. */
    requiresOrgNameMatch?: string;
}

export interface NavChild {
    name: string;
    /** Iconify slug without the `lucide:` prefix. */
    icon: string;
    href?: string;
    /** Shows a hover flyout of these links instead of navigating the row
     * itself — for an item that fans out to several destinations rather
     * than having one of its own (e.g. Dashboards' Sigma dashboards).
     * Mutually exclusive with `href` in practice. */
    flyout?: NavChildFlyoutItem[];
    /** Rendered greyed-out and inert — no destination exists yet. */
    comingSoon?: boolean;
    /** Only rendered when this feature flag is on (mirrors the gating the
     * equivalent item had in the pre-M4S-5697 CMS-driven sidebar). The route
     * itself is enforced by `FEATURE_FLAGGED_ROUTES` in `routeAccess.ts`. */
    requiresFlag?: string;
    /** Ask DANI specifically: visible with per-user or org-trial access, not a flag. */
    requiresDaniAccess?: boolean;
    /** Real destination for internal (@mfour.com) users only — everyone else
     * sees this item rendered `comingSoon` regardless of the `href`/
     * `comingSoon` set above, which apply to non-internal users. */
    internalHref?: string;
    /** Hidden entirely (not greyed out) for anyone but internal (@mfour.com)
     * viewers, View-as included. */
    internalOnly?: boolean;
    /** `internalHref` opens only for an internal viewer who also holds this
     * flag; other staff see the row as a client does. */
    internalRequiresFlag?: string;
    /** Opens the Synthesize channels wizard modal instead of navigating —
     * see `synthesize-channels-modal.tsx`. */
    opensSynthesizeModal?: boolean;
    /** Plain-text hover tooltip shown only to internal (@mfour.com) users —
     * roadmap/status context for items whose real-world state isn't obvious
     * from the label alone. For an item that's also `comingSoon`/gated by
     * `internalHref`, this replaces the default "Coming soon" tooltip. */
    internalTooltip?: string;
}

export interface NavSection {
    key: string;
    label: string;
    /** Iconify slug without the `lucide:` prefix. */
    icon: string;
    color: string;
    /** Overrides `color` for the icon glyph itself in the light-tint header
     * chip (expanded nav + hover peek) — the stage green is too dark to read
     * well against its own pastel background at full saturation. */
    iconColor?: string;
    /** Overrides the derived `${color}22` tint for the header chip's own
     * background, when the reference swatch doesn't match that formula. */
    chipBg?: string;
    children: NavChild[];
}

/**
 * The left-hand nav's IA, from the "MFour Studio JTBD redesign" reference
 * screenshots (permanent since M4S-5697). Section order here (Create,
 * Analyze, Explore, Report) matches `@home`'s HOME_STAGES order. Colors are
 * imported from `@home` so the two surfaces can't drift.
 */
export const NAV_SECTIONS: NavSection[] = [
    {
        key: 'create',
        label: 'Field',
        icon: 'wand-sparkles',
        color: STAGE_COLORS.create,
        children: [
            {
                name: 'Feasibility',
                icon: 'target',
                comingSoon: true,
                internalHref: Routes.FEASIBILITY,
                // Legacy or DANI Feasibility while `feasibility-dani` is on;
                // a plain link to the legacy tool while it is off.
                flyout: FEASIBILITY_CHOICES,
                internalTooltip: 'Roadmap v2.1 (Internal Only)',
            },
            {
                name: 'Builder',
                icon: 'pencil-ruler',
                href: Routes.BUILDER,
                internalTooltip: 'Roadmap v2.2 (External Legacy DIY)',
            },
            { name: 'Status', icon: 'clipboard-list', href: Routes.MY_SURVEYS },
        ],
    },
    {
        key: 'analyze',
        label: 'Analyze',
        icon: 'bar-chart-3',
        color: STAGE_COLORS.analyze,
        children: [
            {
                name: 'Videos',
                icon: 'clapperboard',
                href: Routes.MY_SURVEYS_WITH_MULTIMEDIA,
            },
            { name: 'Crosstabs', icon: 'table-2', href: Routes.CROSSTABS },
            {
                // Data Prep (internal only): tools that prepare survey data before analysis.
                // Hidden entirely for clients and for staff viewing as a client. OE Coding
                // (M4A2A-471) is its first tool; `/oe-coding` is also in INTERNAL_ROUTES, so
                // route access refuses anyone else who reaches the URL.
                name: 'Data Prep',
                icon: 'wrench',
                internalOnly: true,
                internalTooltip: 'Data preparation tools (Internal Only)',
                flyout: [{ name: 'OE Coding', icon: 'tag', href: '/oe-coding' }],
            },
            {
                name: 'Ask DANI',
                icon: 'sparkles',
                href: Routes.STUDIO_AI,
                requiresDaniAccess: true,
            },
        ],
    },
    {
        key: 'explore',
        label: 'Connect',
        icon: 'database',
        color: STAGE_COLORS.explore,
        iconColor: '#48996B',
        chipBg: '#EFFAF4',
        children: [
            {
                name: 'Consumer Data',
                icon: 'database',
                href: '/ai/studio-catalog',
                requiresFlag: STUDIO_NAV_FEATURE_FLAGS.STUDIO_CATALOG,
            },
            // Mirrors the Home screen's Connect drill-down groups
            // (`@home/constants.ts`'s `categories`) — same names, icons, and
            // seed prompts, so the two surfaces can't drift. Each channel
            // has no page of its own, so the flyout seeds Ask DANI directly
            // (same `?seed=` mechanism the Home tiles use), like Dashboards'
            // flyout links to its own Sigma dashboards.
            {
                name: 'Profiles',
                icon: 'id-card',
                flyout: [
                    {
                        name: 'Demographics',
                        icon: 'user-circle-2',
                        href: '/connect/demographics',
                    },
                    {
                        name: 'Attitudes',
                        icon: 'brain',
                        href: '/connect/attitudes',
                    },
                    {
                        name: 'Monthly Opinions',
                        icon: 'calendar',
                        href: '/connect/monthly-opinions',
                    },
                ],
            },
            {
                name: 'Behavior',
                icon: 'footprints',
                flyout: [
                    {
                        name: 'Location',
                        icon: 'map-pin',
                        href: '/connect/location',
                    },
                    {
                        name: 'App',
                        icon: 'smartphone',
                        href: '/connect/app',
                    },
                    {
                        name: 'Web',
                        icon: 'globe',
                        href: '/connect/web',
                    },
                    {
                        name: 'Purchase',
                        icon: 'receipt',
                        href: '/connect/purchase',
                    },
                    {
                        name: 'ChatGPT',
                        icon: 'sparkles',
                        href: '/connect/gen-ai-chatgpt',
                    },
                    {
                        name: 'Gemini',
                        icon: 'sparkles',
                        href: '/connect/gen-ai-gemini',
                        // Held back for stakeholder review — matches the
                        // Home screen's Behavior tile (`@home/constants.ts`).
                        comingSoon: true,
                    },
                ],
            },
            {
                name: 'Exposure',
                icon: 'radio-tower',
                flyout: [
                    {
                        name: 'Digital Ads',
                        icon: 'megaphone',
                        href: '/connect/digital-ads',
                    },
                    {
                        name: 'Media',
                        icon: 'play-circle',
                        href: '/connect/media',
                    },
                    {
                        name: 'Audio',
                        icon: 'headphones',
                        href: '/connect/audio',
                    },
                ],
            },
            { name: 'Synthesize channels', icon: 'infinity', opensSynthesizeModal: true },
        ],
    },
    {
        key: 'report',
        label: 'Report',
        icon: 'presentation',
        color: STAGE_COLORS.report,
        children: [
            { name: 'My Work', icon: 'folder-open', href: Routes.MY_WORK },
            {
                name: 'Dashboards',
                icon: 'layout-dashboard',
                // The three dashboard hubs (`@dashboards/hubs.ts`), then each
                // client's own vTracker (org-matched), as the Home screen's
                // Dashboards tile lists them (`@home/constants.ts`).
                flyout: [...DASHBOARD_HUB_LINKS, ...VTRACKER_DASHBOARDS],
            },
            {
                name: 'Slide Creator',
                icon: 'presentation',
                comingSoon: true,
                internalHref: Routes.SLIDE_CREATION,
                // Internal alpha (M4S-5702): named testers only.
                internalRequiresFlag: SLIDE_CREATOR_ALPHA_FLAG,
                internalTooltip: 'Roadmap v2.1 (Internal Only)',
            },
            {
                name: 'Board Reports',
                icon: 'file-bar-chart',
                href: Routes.REPORTS,
                requiresFlag: STUDIO_NAV_FEATURE_FLAGS.REPORTS,
            },
        ],
    },
];
